import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GET, POST, httpOk } from 'helpers/api'

function Index() {
	const { t } = useTranslation()

	const [botToken, setBotToken]       = useState('')
	const [testChatId, setTestChatId]   = useState('')
	const [saving, setSaving]           = useState(false)
	const [testing, setTesting]         = useState(false)
	const [saveStatus, setSaveStatus]   = useState(null)  // 'ok' | 'err'
	const [testStatus, setTestStatus]   = useState(null)  // 'ok' | 'err' | null
	const [testMessage, setTestMessage] = useState('')
	const [hasToken, setHasToken]       = useState(false)

	useEffect(() => {
		loadSettings()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	async function loadSettings() {
		const res = await GET('/telegram-settings', {}, { loader: true })
		if (httpOk(res)) {
			const token = res.data?.bot_token || ''
			setBotToken(token)
			setHasToken(!!token)
		}
	}

	async function saveToken(e) {
		e.preventDefault()
		setSaving(true)
		setSaveStatus(null)
		const res = await POST('/telegram-settings', { bot_token: botToken }, { loader: false })
		setSaving(false)
		if (httpOk(res)) {
			setSaveStatus('ok')
			setHasToken(!!botToken.trim())
			setTimeout(() => setSaveStatus(null), 3000)
		} else {
			setSaveStatus('err')
		}
	}

	async function sendTest(e) {
		e.preventDefault()
		setTesting(true)
		setTestStatus(null)
		setTestMessage('')
		const res = await POST('/telegram-settings/test', { chat_id: testChatId }, { loader: false })
		setTesting(false)
		if (httpOk(res)) {
			setTestStatus('ok')
			setTestMessage(t('tg_test_success'))
		} else {
			setTestStatus('err')
			setTestMessage(res?.data?.message || t('tg_test_failed'))
		}
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('telegram_settings')}
				</h4>
			</div>

			<div className="row">
				<div className="col-md-7 col-lg-6">

					{/* Step 1 — How to get bot token */}
					<div className="card mb-3">
						<div className="card-body">
							<h6 className="fw-bold mb-3">
								<span className="badge bg-primary me-2">1</span>
								{t('tg_step1_title')}
							</h6>
							<ol className="ps-3 mb-0" style={{ lineHeight: '1.9', color: '#555', fontSize: '14px' }}>
								<li>{t('tg_step1_1')} <a href="https://t.me/BotFather" target="_blank" rel="noreferrer">@BotFather</a></li>
								<li>{t('tg_step1_2')}: <code>/newbot</code></li>
								<li>{t('tg_step1_3')}</li>
								<li>{t('tg_step1_4')}</li>
							</ol>
						</div>
					</div>

					{/* Step 2 — Enter bot token */}
					<div className="card mb-3">
						<div className="card-body">
							<h6 className="fw-bold mb-3">
								<span className="badge bg-primary me-2">2</span>
								{t('tg_step2_title')}
							</h6>

							{hasToken && (
								<div className="alert alert-success py-2 mb-3" style={{ fontSize: '13px' }}>
									<i className="uil uil-check-circle me-1"></i>
									{t('tg_token_saved')}
								</div>
							)}

							<form onSubmit={saveToken} autoComplete="off">
								<div className="form-group mb-3">
									<label className="form-label">{t('tg_bot_token')}</label>
									<input
										type="text"
										className="form-control font-monospace"
										placeholder="1234567890:AABBcc..."
										value={botToken}
										onChange={e => setBotToken(e.target.value)}
										style={{ fontSize: '13px', letterSpacing: '0.5px' }}
									/>
									<div className="form-text">{t('tg_token_hint')}</div>
								</div>

								{saveStatus === 'ok' && (
									<div className="alert alert-success py-2 mb-2" style={{ fontSize: '13px' }}>
										<i className="uil uil-check me-1"></i> {t('saved')}
									</div>
								)}
								{saveStatus === 'err' && (
									<div className="alert alert-danger py-2 mb-2" style={{ fontSize: '13px' }}>
										<i className="uil uil-exclamation-triangle me-1"></i> {t('tg_save_error')}
									</div>
								)}

								<button
									type="submit"
									className="btn btn-primary"
									disabled={saving}>
									{saving
										? <><span className="spinner-border spinner-border-sm me-2"></span>{t('saving')}</>
										: t('save')}
								</button>
							</form>
						</div>
					</div>

					{/* Step 3 — Get chat id */}
					<div className="card mb-3">
						<div className="card-body">
							<h6 className="fw-bold mb-3">
								<span className="badge bg-primary me-2">3</span>
								{t('tg_step3_title')}
							</h6>
							<ol className="ps-3 mb-0" style={{ lineHeight: '1.9', color: '#555', fontSize: '14px' }}>
								<li>{t('tg_step3_1')}</li>
								<li>{t('tg_step3_2')}: <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer">@userinfobot</a></li>
								<li>{t('tg_step3_3')}</li>
							</ol>
						</div>
					</div>

					{/* Step 4 — Test connection */}
					<div className="card mb-3">
						<div className="card-body">
							<h6 className="fw-bold mb-3">
								<span className="badge bg-success me-2">4</span>
								{t('tg_step4_title')}
							</h6>
							<form onSubmit={sendTest} autoComplete="off">
								<div className="form-group mb-3">
									<label className="form-label">{t('tg_chat_id')}</label>
									<input
										type="text"
										className="form-control font-monospace"
										placeholder="-1001234567890"
										value={testChatId}
										onChange={e => setTestChatId(e.target.value)}
										style={{ fontSize: '13px' }}
									/>
									<div className="form-text">{t('tg_chatid_hint')}</div>
								</div>

								{testStatus === 'ok' && (
									<div className="alert alert-success py-2 mb-2" style={{ fontSize: '13px' }}>
										<i className="uil uil-check-circle me-1"></i> {testMessage}
									</div>
								)}
								{testStatus === 'err' && (
									<div className="alert alert-danger py-2 mb-2" style={{ fontSize: '13px' }}>
										<i className="uil uil-exclamation-triangle me-1"></i> {testMessage}
									</div>
								)}

								<button
									type="submit"
									className="btn btn-outline-primary"
									disabled={testing || !hasToken || !testChatId.trim()}>
									{testing
										? <><span className="spinner-border spinner-border-sm me-2"></span>{t('tg_testing')}</>
										: <><i className="uil uil-telegram me-1"></i>{t('tg_send_test')}</>}
								</button>
							</form>
						</div>
					</div>

					{/* Info — Where to use Chat ID */}
					<div className="card border-0" style={{ background: '#f8f9fa' }}>
						<div className="card-body py-3">
							<p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
								<i className="uil uil-info-circle me-1 text-primary"></i>
								{t('tg_usage_hint')}
							</p>
						</div>
					</div>

				</div>
			</div>
		</>
	)
}

export default Index
